import { assertCents, type Cents } from './money'

/**
 * 平台配置的值、边界与校验 —— **一处定义,三个消费方**:
 *   · apps/api 的 PlatformConfigService(读库、写库前都过这里)
 *   · 运营端的配置页(在提交前就能说清哪个数不合法,而不是等 400)
 *   · 启动引导(建配置行时用的初值)
 *
 * ═══ 为什么这一坨要单独占一个文件 ═══
 *
 * L0-05 的硬要求是「窗口时长与开关必须是配置项而不是硬编码」。
 * 在环境变量时代,那条要求的落地方式是「配错了服务拒绝启动」——
 * 也就是**在部署那一刻拦住写坏的值**。挪进表之后部署那一刻没有了,
 * 等价的约束只能落在**写入口**:改配置的接口拒绝非法值,并说清哪个字段哪里不合法。
 *
 * 静默退回默认值是这个迁移里最容易犯的错,而它的表现恰好是最坏的那种:
 * 运营以为自己把窗口调成了 30 分钟,实际上库里存进去的是别的东西,
 * 而服务照常跑、接口照常 200。将来要向合作方解释「为什么抢不到」时,
 * 这一类失效没有任何痕迹。所以校验函数返回的是**逐字段的问题列表**,
 * 由调用方原样说给运营听,而不是一个笼统的 false。
 */

/**
 * 平台配置的面值(契约 components.schemas.PlatformConfig 的投影)。
 *
 * ═══ 2026-10-11:两组字段按人裁决整块删掉,而且**不许加回来** ═══
 *
 * ① 监管端「IP 白名单」的开关与条目(M-07):裁决「去掉,不限制 IP」——
 *    本期没有对监管端做来源网络限制的需求,留一个没人开启的开关只会把
 *    「开关在库里存在、在行为上不存在」这类失效挂在配置面上。
 * ② 代办服务费的「费率形态」(M-13):裁决代办费是后台配置的一个**固定数额**,
 *    实付 = 代办费 + 实时计算的运费;费率会让每一单的代办费随运费浮动,
 *    而甲方要的是一个说好的数。
 *
 * 裁决原文、理由与**重开条件**在 decisions.json 的 M-07 / M-13;
 * 撤走经过在 docs/fixes/2026-10-11-removals.md。反向守卫
 * guards/check-removed-config.mjs 盯着这两个名字不许再出现在生产源码、契约与表结构里 ——
 * 将来真要重开,先删掉那条守卫(那是一个看得见的动作)。
 */
export interface PlatformConfigValues {
  /** 窗口开关。将来国企合作方问「为什么抢不到」时,要能证明规则一致适用(L0-05) */
  priorityWindowEnabled: boolean
  /** 窗口时长,分钟 */
  priorityWindowMinutes: number
  /** 空跑费,分(M-12)。全额归承运商 */
  deadRunFee: Cents
  /** 代办服务费,分(M-13)。**后台配置的固定数额** —— 全流程代办单实付 = 它 + 实时计算的运费;纯运输单恒为 0 */
  agencyFeeDefault: Cents
  /**
   * 自动签收天数(S-55,Y-47)。**从送达时刻起算**,过这么多天仍未签收即视为自动签收。
   *
   * 它是配置项而不是硬编码,理由与窗口时长一样(L0-05):签收一旦成为完成的闸门,
   * 这个数就直接决定「钱多久能结算」。运营要能当场把它调大调小,
   * 而不是等一次发版 —— 存量订单靠它自然解开(送达已久的老单到点自动签收)。
   * 0 = 送达即到期(探针用它造「送达即自动签收」这一格)。
   */
  autoSignDays: number
  /**
   * 评价窗口天数(S-57,Y-50 / Y-55 共用同一个键)。**一个键管两件事**:
   *   ① 可评窗口:订单完成之后这么多天内可以提交评价,过期不可评(Y-55);
   *   ② 盲评解锁:一方提交、另一方一直不评,这么多天后已提交的那条**单向解锁**(Y-50)。
   *
   * 它是配置项而不是硬编码,理由与 autoSignDays 一样:这个数决定「一条评价多久之后
   * 才会被对方看到」,运营要能当场调它。0 = 完成即到解锁点(探针用它造「到点解锁」那一格)。
   */
  reviewWindowDays: number
}

/**
 * 配置行的初值 —— 也就是「谁都没改过」时的样子。
 *
 * 窗口 = 开启 + 15 分钟,与 S-06 期间环境变量时代的默认值一致;
 * 空跑费与代办费初值都是 0(M-12 的空跑费金额「是配置项」但 BLK-01 还没给数字;
 * M-13 的代办费原文就是「前期可能不收费」,科目建好、初值 0)。
 *
 * 它只在**建那一行**的时候被用到一次。读路径**不许**在缺行时退回这里 ——
 * 「读不到配置就按默认来」把「配置坏了/没建」翻译成「一切正常」,
 * 而那正是运营以为自己改了、实际没改的那种失效。
 */
export const PLATFORM_CONFIG_INITIAL: PlatformConfigValues = {
  priorityWindowEnabled: true,
  priorityWindowMinutes: 15,
  deadRunFee: 0,
  agencyFeeDefault: 0,
  // 自动签收天数初值(S-55,Y-47)。7 天是这一轮选的合理默认:比它短会让收件人
  // 还没来得及确认就被自动签收,比它长则让钱挂在平台账上太久。它不是业务规则,
  // 是「谁都没改过」时的样子 —— 运营随时可调。
  autoSignDays: 7,
  // 评价窗口 / 解锁窗口初值(S-57,Y-50 / Y-55)。7 天与 autoSignDays 同口径:
  // 一条评价在双方都提交前互不可见,一方不评则 7 天后单向解锁 —— 比它短会让
  // 盲评形同虚设(对方还没写就被解锁了),比它长则让先写的人等太久。
  reviewWindowDays: 7,
}

/**
 * 数值边界。**这些是防笔误的,不是业务规则** —— 业务规则(具体几小时)在 BLK-01 那边。
 *
 * 每个边界都写清「不放这个上限会怎样」,因为一个说不出理由的上限
 * 迟早会被人当成拦路的摆设删掉:
 *
 *   priorityWindowMinutes ≤ 1440(一天)
 *     窗口关不掉只是抢不到单;写错一位变成 15000 分钟(10 天)时,
 *     整个订单池对非自营承运商静默消失十天,而没有任何一处报错。
 *     1440 的含义是「这个值不可能超过一天」—— 超过它就一定是笔误。
 *
 *   金额 ≤ 99_999_999 分(不到 100 万元)
 *     空跑费与代办费都是单笔金额。100 万元的代办费或者空跑费不是业务,
 *     是少打了一个小数点或按错了键。不放上限的话它会一路进计价快照、
 *     进客户实付、进结算,而在那之前没有任何一处会拦。
 */
export const PLATFORM_CONFIG_LIMITS = {
  priorityWindowMinutes: { min: 0, max: 1440 },
  feeCents: { min: 0, max: 99_999_999 },
  /**
   * 自动签收天数 ≤ 90。
   *
   * 不放上限的话,一次笔误(比如 700)会让一批单在两年后才自动签收 ——
   * 而「钱挂在平台账上」这件事没有任何界面会主动说。90 天是「这个值不可能再大」的
   * 边界:超过它一定是多打了一位或按错了键,而不是业务上真要等三个月。
   */
  autoSignDays: { min: 0, max: 90 },
  /**
   * 评价窗口 / 解锁窗口天数 ≤ 90。
   *
   * 不放上限的话,一次笔误(比如 700)会让一条评价在两年后才解锁 —— 而「先写评价的人
   * 等了很久才被对方看到」这件事没有任何界面会主动说。90 天是「这个值不可能再大」的边界。
   */
  reviewWindowDays: { min: 0, max: 90 },
} as const

/** 校验结果:要么是干净的 values,要么是一串能给运营读的问题 */
export type PlatformConfigValidation =
  | { ok: true; value: PlatformConfigValues }
  | { ok: false; problems: string[] }

/**
 * 校验一份配置(来自 HTTP 请求体或库里那一行)。**纯函数,不读库不看时钟。**
 *
 * 两个方向都要用:
 *   · 写入口 —— 拒绝非法值,problems 直接进 400 的 message
 *   · 读路径 —— 库里那一行如果被人手工改坏了,读的时候当场炸(500 + 日志),
 *     而不是把坏值当真值拿去算窗口
 *
 * 未知字段一律拒绝,不做「忽略掉不认识的键继续」。忽略的后果是运营端
 * 加了一个新配置项、后端这版还不认识,提交之后看起来成功了 ---- 而新项从未生效。
 */
export function validatePlatformConfig(input: unknown): PlatformConfigValidation {
  const problems: string[] = []

  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, problems: ['配置必须是一个 JSON 对象'] }
  }
  const o = input as Record<string, unknown>
  const known = new Set(Object.keys(PLATFORM_CONFIG_INITIAL))
  for (const key of Object.keys(o)) {
    if (!known.has(key)) problems.push(`不认识的配置项「${key}」—— 契约里的平台配置只有 ${[...known].join(' / ')}`)
  }

  const enabled = o['priorityWindowEnabled']
  if (typeof enabled !== 'boolean') {
    problems.push(`priorityWindowEnabled 必须是 true 或 false,实际拿到 ${JSON.stringify(enabled)}`)
  }

  const minutes = o['priorityWindowMinutes']
  const minuteLimit = PLATFORM_CONFIG_LIMITS.priorityWindowMinutes
  if (!Number.isSafeInteger(minutes) || (minutes as number) < minuteLimit.min || (minutes as number) > minuteLimit.max) {
    problems.push(
      `priorityWindowMinutes 必须是 ${minuteLimit.min} ~ ${minuteLimit.max} 之间的整数分钟,实际拿到 ${JSON.stringify(minutes)}。` +
        `上限 ${minuteLimit.max} 是一天 —— 比一天还长的「优先窗口」一定是笔误,而它会静默地把订单池对非自营关掉那么久(L0-05)`,
    )
  }

  for (const field of ['deadRunFee', 'agencyFeeDefault'] as const) {
    const v = o[field]
    const limit = PLATFORM_CONFIG_LIMITS.feeCents
    if (!Number.isSafeInteger(v) || (v as number) < limit.min || (v as number) > limit.max) {
      problems.push(
        `${field} 必须是 ${limit.min} ~ ${limit.max} 之间的整数分(M-14:金额一律整数分),实际拿到 ${JSON.stringify(v)}`,
      )
    }
  }

  const autoSign = o['autoSignDays']
  const autoLimit = PLATFORM_CONFIG_LIMITS.autoSignDays
  if (
    !Number.isSafeInteger(autoSign) ||
    (autoSign as number) < autoLimit.min ||
    (autoSign as number) > autoLimit.max
  ) {
    problems.push(
      `autoSignDays 必须是 ${autoLimit.min} ~ ${autoLimit.max} 之间的整数天(S-55 / Y-47),实际拿到 ${JSON.stringify(autoSign)}。` +
        `上限 ${autoLimit.max} 天是防笔误 —— 比它还长的「自动签收」一定是多打了一位,` +
        '而它会让那批单的钱在平台账上多挂那么久,没有任何界面会主动说这件事',
    )
  }

  const reviewWindow = o['reviewWindowDays']
  const reviewLimit = PLATFORM_CONFIG_LIMITS.reviewWindowDays
  if (
    !Number.isSafeInteger(reviewWindow) ||
    (reviewWindow as number) < reviewLimit.min ||
    (reviewWindow as number) > reviewLimit.max
  ) {
    problems.push(
      `reviewWindowDays 必须是 ${reviewLimit.min} ~ ${reviewLimit.max} 之间的整数天(S-57 / Y-50 / Y-55),实际拿到 ${JSON.stringify(reviewWindow)}。` +
        `上限 ${reviewLimit.max} 天是防笔误 —— 比它还长的「评价窗口」一定是多打了一位,` +
        '而它会让一条评价很久之后才被对方看到,没有任何界面会主动说这件事',
    )
  }

  if (problems.length > 0) return { ok: false, problems }

  return {
    ok: true,
    value: {
      priorityWindowEnabled: enabled as boolean,
      priorityWindowMinutes: minutes as number,
      deadRunFee: o['deadRunFee'] as Cents,
      agencyFeeDefault: o['agencyFeeDefault'] as Cents,
      autoSignDays: autoSign as number,
      reviewWindowDays: reviewWindow as number,
    },
  }
}

/**
 * 校验一份**已经通过 validatePlatformConfig** 的配置里那两个金额。
 * 单独拎出来给计价链路的调用方用 —— computeQuote 拿到的只有代办费那一个数。
 *
 * 它抛而不是返回 false:计价链路上一个坏掉的金额必须当场炸。
 * 悄悄当成 0 的后果是「代办费配置写坏了」表现成「这单代办费是 0」——
 * 客户少付一笔钱,而账目全都自洽。
 */
export function assertPlatformFeeCents(value: unknown, what: string): asserts value is Cents {
  const limit = PLATFORM_CONFIG_LIMITS.feeCents
  if (!Number.isSafeInteger(value) || (value as number) < limit.min || (value as number) > limit.max) {
    throw new Error(
      `${what}必须是 ${limit.min} ~ ${limit.max} 之间的整数分(M-14),实际拿到 ${JSON.stringify(value)}`,
    )
  }
  assertCents(value, what)
}
